import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import {
  Alert,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  Cell,
} from 'recharts';

function ShibaPage() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [shibaList, setShibaList] = useState({});
  const [loading, setLoading] = useState(false);

  // 開催場とレース番号のプルダウン
  const RACE_OPTIONS = [
    '札幌',
    '函館',
    '福島',
    '新潟',
    '中山',
    '東京',
    '中京',
    '京都',
    '阪神',
    '小倉',
  ];
  const RACE_NUM_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

  // 今日(ローカル)を YYYY-MM-DD
  const todayStr = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();

  const [raceDate, setRaceDate] = useState(todayStr);
  const [raceInfo, setRaceInfo] = useState('東京');
  const [raceNum, setRaceNum] = useState('11');

  // キャプチャ用（必要なら）
  const chartRef = useRef(null);
  const [saveMsg, setSaveMsg] = useState(null);

  const API_BASE = '';

  const pct = (v) => `${v}%`;
  const formatPercent = (value) =>
    value === undefined || value === null
      ? '-'
      : Number((Math.floor(Number(value) * 1000) / 10).toFixed(1));

  const gradeHorse = (probability, expectedValue) => {
    if (probability === undefined || expectedValue === undefined) return '-';
    if (probability >= 0.28 && expectedValue >= 1.3) return 'S';
    if (probability >= 0.2 && expectedValue >= 1.15) return 'A';
    if (probability >= 0.13 && expectedValue >= 1.05) return 'B';
    if (probability >= 0.07 && expectedValue >= 0.95) return 'C';
    if (probability >= 0.03 || expectedValue >= 0.9) return 'D';
    return 'E';
  };

  const gradeColors = {
    S: '#d32f2f',
    A: '#f57c00',
    B: '#fbc02d',
    C: '#1976d2',
    D: '#7b1fa2',
    E: '#9e9e9e',
  };

  const buildChartData = (list) =>
    (list?.tanpuku_df ?? [])
      .map((r) => {
        const p = Number(r.p ?? r.p_cal);
        const probabilityPct = formatPercent(p);
        const expectedValue = Number(r.ev);
        const gradeFromServer = (() => {
          const raw =
            r.grade ||
            r.Grade ||
            r.ranks ||
            r.rank ||
            (typeof r.grade === 'string' && r.grade.trim());
          if (!raw) return undefined;
          const g = String(raw).trim().toUpperCase();
          return ['S', 'A', 'B', 'C', 'D', 'E'].includes(g) ? g : undefined;
        })();
        const grade = gradeFromServer || gradeHorse(p, expectedValue);
        return {
          umaban: r.umaban,
          pCal: p,
          probability: probabilityPct,
          odds: Number(r.tansho_odds ?? r.odds),
          ev: expectedValue,
          evPercent: formatPercent(expectedValue),
          prediction:
            r.pick_ev_mu === true || Number(r.pick) === 1 || Number(r.ev_gt_1_plus_mu) === 1,
          grade,
        };
      })
      .sort((a, b) => b.pCal - a.pCal);

  const sortedHorses = useMemo(() => buildChartData(shibaList), [shibaList]);
  const sRankHorses = useMemo(() => sortedHorses.filter((h) => h.grade === 'S'), [sortedHorses]);
  const chartData = useMemo(() => sortedHorses.slice(0, 8), [sortedHorses]);
  const barColor = (grade) => gradeColors[grade] || '#8884d8';
  const raceName = shibaList?.race_name;

  const fetchRaceData = async () => {
    try {
      setLoading(true);
      const combined = `${raceInfo}${raceNum}`; // 例: 東京11
      const res = await axios.get(`${API_BASE}/api/youtube/download`, {
        params: { race_date: raceDate, race_info: combined },
      });
      setShibaList(res.data);
      setSaveMsg(null);
    } catch (error) {
      console.error('データ取得失敗:', error);
      setSaveMsg({ type: 'error', text: 'データ取得に失敗しました。' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 初期表示用に当日東京11Rを取得
    fetchRaceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box p={{ xs: 0, sm: 4 }} sx={{ width: '100%' }}>
      {/* フィルター（日時・開催場・レース番号） */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          label="日付"
          type="date"
          value={raceDate}
          onChange={(e) => setRaceDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 220 } }}
        />

        <FormControl sx={{ width: { xs: '100%', sm: 220 } }}>
          <InputLabel id="race-info-label">開催場</InputLabel>
          <Select
            labelId="race-info-label"
            label="開催場"
            value={raceInfo}
            onChange={(e) => setRaceInfo(e.target.value)}
          >
            {RACE_OPTIONS.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: { xs: '100%', sm: 160 } }}>
          <InputLabel id="race-num-label">レース番号</InputLabel>
          <Select
            labelId="race-num-label"
            label="レース番号"
            value={raceNum}
            onChange={(e) => setRaceNum(e.target.value)}
          >
            {RACE_NUM_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" color="primary" onClick={fetchRaceData} disabled={loading}>
          {loading ? '取得中…' : '取得'}
        </Button>
      </Stack>

      {saveMsg && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={saveMsg.type} sx={{ whiteSpace: 'pre-line' }}>
            {saveMsg.text}
          </Alert>
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          今レースS評価
        </Typography>
        {sRankHorses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            S評価の馬はまだいません。
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>馬番</TableCell>
                <TableCell>オッズ</TableCell>
                <TableCell>単勝確率</TableCell>
                <TableCell>期待値</TableCell>
                <TableCell>購入可否</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sRankHorses.map((h, idx) => (
                <TableRow key={h.id ?? `${h.umaban}-${idx}`}>
                  <TableCell>{h.umaban}</TableCell>
                  <TableCell>{h.odds}</TableCell>
                  <TableCell>{h.probability}%</TableCell>
                  <TableCell>{h.evPercent}%</TableCell>
                  <TableCell align="center">{h.prediction ? '◎' : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {chartData.length > 0 && (
        <Box
          sx={{
            width: { xs: '100%', sm: '100%' },
            ml: { xs: 'calc(50% - 50vw)', sm: 0 },
            mr: { xs: 'calc(50% - 50vw)', sm: 0 },
            height: { xs: 420, sm: 380 },
            mb: { xs: 8, sm: 6 },
          }}
        >
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              単勝確率（%）上位8頭 {raceName ? `（${raceName}）` : ''}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              色の意味
            </Typography>
            {['S', 'A', 'B', 'C', 'D', 'E'].map((g) => (
              <Stack key={g} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: gradeColors[g],
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {g}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <div ref={chartRef}>
            <ResponsiveContainer width="100%" height={isXs ? 440 : 380}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 24, left: 8, bottom: isXs ? 60 : 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="umaban"
                  interval={0}
                  tick={{ fontSize: isXs ? 10 : 18 }}
                  height={isXs ? 44 : 30}
                  angle={isXs ? -35 : 0}
                  textAnchor={isXs ? 'end' : 'middle'}
                />
                <YAxis domain={[0, 100]} tickFormatter={pct} tick={{ fontSize: isXs ? 10 : 18 }} />
                <Tooltip
                  wrapperStyle={{ fontSize: isXs ? '0.7rem' : '0.85rem' }}
                  formatter={(value, name) => {
                    if (name === 'probability') return [`${value}%`, '単勝確率'];
                    if (name === 'grade') return [value, '評価'];
                    return [value, name];
                  }}
                  labelFormatter={(label, payload) => {
                    const p = payload?.[0]?.payload;
                    return `馬番 ${label}${p?.prediction ? '（◎）' : ''}`;
                  }}
                />
                <Bar dataKey="probability" name="単勝確率">
                  <LabelList
                    dataKey="probability"
                    position="top"
                    formatter={(v) => `${v}%`}
                    fontSize={isXs ? 8 : 12}
                  />
                  {chartData.map((d, i) => (
                    <Cell key={`cell-${i}`} fill={barColor(d.grade)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ mt: { xs: 0, sm: 20 } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                オッズ
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                単勝確率
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                期待値
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                推奨馬
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chartData.map((tanpuku, idx) => (
              <TableRow key={tanpuku.id ?? `${tanpuku.umaban}-${idx}`}>
                <TableCell align="center">{tanpuku.umaban}番</TableCell>
                <TableCell align="center">{tanpuku.odds}</TableCell>
                <TableCell align="center">{tanpuku.probability}%</TableCell>
                <TableCell align="center">{tanpuku.evPercent}%</TableCell>
                <TableCell align="center">{tanpuku.prediction ? '◎' : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ShibaPage;
